import React, { useEffect, useState, useRef } from 'react';
import { useTutorial } from '../../contexts/TutorialContext';
import styles from './TutorialOverlay.module.css';

interface TutorialOverlayProps {
  children: React.ReactNode;
}

const TutorialOverlay: React.FC<TutorialOverlayProps> = ({ children }) => {
  const { currentStep, isActive, completeStep, skipStep, closeTutorial } = useTutorial();
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isActive || !currentStep) {
      setTargetElement(null);
      return;
    }

    // Find target element
    if (currentStep.targetElement) {
      const element = document.querySelector(currentStep.targetElement) as HTMLElement;
      if (element) {
        setTargetElement(element);
        calculateTooltipPosition(element);
      } else {
        // Element not found, wait a bit and try again
        const timeout = setTimeout(() => {
          const retryElement = document.querySelector(currentStep.targetElement!) as HTMLElement;
          if (retryElement) {
            setTargetElement(retryElement);
            calculateTooltipPosition(retryElement);
          }
        }, 500);
        return () => clearTimeout(timeout);
      }
    } else {
      // Center position for steps without target element
      setTooltipPosition({
        top: window.innerHeight / 2 - 150,
        left: window.innerWidth / 2 - 200
      });
    }
  }, [currentStep, isActive]);

  const calculateTooltipPosition = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    let top = 0;
    let left = 0;

    switch (currentStep?.position) {
      case 'top':
        top = rect.top + scrollTop - 120;
        left = rect.left + scrollLeft + rect.width / 2 - 200;
        break;
      case 'bottom':
        top = rect.bottom + scrollTop + 20;
        left = rect.left + scrollLeft + rect.width / 2 - 200;
        break;
      case 'left':
        top = rect.top + scrollTop + rect.height / 2 - 75;
        left = rect.left + scrollLeft - 420;
        break;
      case 'right':
        top = rect.top + scrollTop + rect.height / 2 - 75;
        left = rect.right + scrollLeft + 20;
        break;
      case 'center':
      default:
        top = window.innerHeight / 2 - 150;
        left = window.innerWidth / 2 - 200;
        break;
    }

    // Ensure tooltip stays within viewport
    const tooltipWidth = 400;
    const tooltipHeight = 150;

    if (left < 10) left = 10;
    if (left + tooltipWidth > window.innerWidth - 10) {
      left = window.innerWidth - tooltipWidth - 10;
    }
    if (top < 10) top = 10;
    if (top + tooltipHeight > window.innerHeight - 10) {
      top = window.innerHeight - tooltipHeight - 10;
    }

    setTooltipPosition({ top, left });
  };

  const handleComplete = () => {
    if (currentStep) {
      completeStep(currentStep.name);
    }
  };

  const handleSkip = () => {
    if (currentStep) {
      skipStep(currentStep.name);
    }
  };

  const handleClose = () => {
    closeTutorial();
  };

  if (!isActive || !currentStep) {
    return <>{children}</>;
  }

  return (
    <div className={styles.tutorialContainer}>
      {children}
      
      {/* Dark overlay */}
      <div className={styles.overlay} ref={overlayRef}>
        {/* Highlight target element */}
        {targetElement && (
          <div
            className={styles.highlight}
            style={{
              top: targetElement.getBoundingClientRect().top + window.pageYOffset,
              left: targetElement.getBoundingClientRect().left + window.pageXOffset,
              width: targetElement.offsetWidth,
              height: targetElement.offsetHeight,
            }}
          />
        )}
        
        {/* Tutorial tooltip */}
        <div
          className={styles.tooltip}
          style={{
            top: tooltipPosition.top,
            left: tooltipPosition.left,
          }}
        >
          <div className={styles.tooltipHeader}>
            <h3 className={styles.tooltipTitle}>{currentStep.title}</h3>
            <button className={styles.closeButton} onClick={handleClose}>
              ×
            </button>
          </div>
          
          <div className={styles.tooltipContent}>
            <p className={styles.tooltipDescription}>{currentStep.description}</p>
          </div>
          
          <div className={styles.tooltipActions}>
            {currentStep.isOptional && (
              <button className={styles.skipButton} onClick={handleSkip}>
                Skip
              </button>
            )}
            <button className={styles.nextButton} onClick={handleComplete}>
              {currentStep.actionType === 'click' ? 'Got it!' : 'Next'}
            </button>
          </div>
          
          {/* Arrow pointing to target element */}
          {targetElement && currentStep.position !== 'center' && (
            <div className={`${styles.arrow} ${styles[`arrow-${currentStep.position}`]}`} />
          )}
        </div>
      </div>
    </div>
  );
};

export default TutorialOverlay;
